export type RoomKind = "public" | "family";
export type Tackle = "float" | "donka" | "lure";
export type CatchDecision = "keep" | "release";
export const KEEP_LIMIT_KG = 10;

export interface FishSpecies {
  id: string;
  name: string;
  icon: string;
  minKg: number;
  maxKg: number;
  points: number;
  commercialMinKg: number;
  seasons: number[];
  tackle: Tackle[];
  protected?: boolean;
}

export interface CatchResult {
  id: string;
  species: FishSpecies;
  weightKg: number;
  points: number;
  biteDelayMs: number;
  windowMs: number;
  token?: string;
}

export function castPhase(pending: boolean, biteAt: number, reelDeadline: number, now = Date.now()): "idle" | "waiting" | "bite" {
  if (!pending) return "idle";
  return now >= biteAt && now <= reelDeadline ? "bite" : "waiting";
}

export const species: FishSpecies[] = [
  { id: "roach", name: "Vobla", icon: "◖", minKg: 0.15, maxKg: 1.1, commercialMinKg: 0.18, points: 35, seasons: [2, 3, 4], tackle: ["float", "donka"] },
  { id: "bream", name: "Bream", icon: "◒", minKg: 0.4, maxKg: 4.8, commercialMinKg: 0.5, points: 60, seasons: [3, 4, 5, 8, 9, 10], tackle: ["float", "donka"] },
  { id: "perch", name: "Perch", icon: "◗", minKg: 0.2, maxKg: 2.8, commercialMinKg: 0.25, points: 55, seasons: [0, 1, 2, 8, 9, 10, 11], tackle: ["float", "lure"] },
  { id: "zander", name: "Zander", icon: "◁", minKg: 0.7, maxKg: 8.5, commercialMinKg: 1, points: 110, seasons: [2, 3, 4, 8, 9, 10], tackle: ["lure"] },
  { id: "carp", name: "Wild carp", icon: "◉", minKg: 1.1, maxKg: 17, commercialMinKg: 1.5, points: 130, seasons: [4, 5, 6, 7, 8], tackle: ["donka", "float"] },
  { id: "pike", name: "Pike", icon: "◁", minKg: 0.8, maxKg: 12, commercialMinKg: 1, points: 125, seasons: [2, 3, 8, 9, 10], tackle: ["lure"] },
  { id: "catfish", name: "Wels catfish", icon: "〰", minKg: 2, maxKg: 45, commercialMinKg: 3, points: 180, seasons: [4, 5, 6, 7, 8], tackle: ["donka"] },
  { id: "sterlet", name: "Sterlet — conservation sighting", icon: "◇", minKg: 0.8, maxKg: 5, commercialMinKg: 999, points: 250, seasons: [3, 4, 8, 9], tackle: ["donka"], protected: true },
];

export interface DecisionOutcome {
  correct: boolean;
  shouldRelease: boolean;
  reason: "protected" | "undersized" | "limit" | "commercial";
  matchBonus: boolean;
  points: number;
  keptKg: number;
  releasedKg: number;
}

export function evaluateCatchDecision(result: CatchResult, decision: CatchDecision, keptKg: number, tackle: Tackle, month: number): DecisionOutcome {
  const shouldRelease = Boolean(result.species.protected)
    || result.weightKg < result.species.commercialMinKg
    || keptKg + result.weightKg > KEEP_LIMIT_KG;
  const reason = result.species.protected ? "protected"
    : result.weightKg < result.species.commercialMinKg ? "undersized"
    : keptKg + result.weightKg > KEEP_LIMIT_KG ? "limit"
    : "commercial";
  const correct = decision === (shouldRelease ? "release" : "keep");
  const matchBonus = result.species.tackle.includes(tackle) && result.species.seasons.includes(month) ? 50 : 0;
  return {
    correct,
    shouldRelease,
    reason,
    matchBonus: matchBonus > 0,
    points: correct ? result.points + matchBonus : -50,
    keptKg: decision === "keep" && correct ? +(keptKg + result.weightKg).toFixed(2) : keptKg,
    releasedKg: decision === "release" ? result.weightKg : 0,
  };
}

export function astrakhanNow(date = new Date()) {
  return new Date(date.toLocaleString("en-US", { timeZone: "Europe/Astrakhan" }));
}

export function seasonFor(date = new Date()): "Winter" | "Spring" | "Summer" | "Autumn" {
  const month = astrakhanNow(date).getMonth();
  if (month < 2 || month === 11) return "Winter";
  if (month < 5) return "Spring";
  if (month < 8) return "Summer";
  return "Autumn";
}

export function localCatch(tackle: Tackle, now = new Date()): CatchResult {
  const month = astrakhanNow(now).getMonth();
  const available = species.filter((fish) => fish.tackle.includes(tackle) && fish.seasons.includes(month));
  const pool = available.length ? available : species.filter((fish) => fish.tackle.includes(tackle));
  const picked = pool[Math.floor(Math.random() * pool.length)]!;
  const weightKg = +(picked.minKg + Math.random() * (picked.maxKg - picked.minKg) * 0.42).toFixed(2);
  return {
    id: crypto.randomUUID(),
    species: picked,
    weightKg,
    points: Math.round(picked.points * Math.max(0.6, weightKg / Math.max(1, picked.minKg * 2))),
    biteDelayMs: 1700 + Math.floor(Math.random() * 4400),
    windowMs: 1250,
  };
}

export function weatherFor(date = new Date()) {
  const local = astrakhanNow(date);
  const seed = Number(`${local.getFullYear()}${local.getMonth() + 1}${local.getDate()}`);
  const conditions = ["Clear current", "Crosswind", "Light rain", "Falling pressure", "Calm water"];
  const index = seed % conditions.length;
  const month = local.getMonth();
  const monthly = [-5, -4, 3, 13, 21, 27, 30, 28, 21, 12, 4, -2];
  const temp = monthly[month]! + ((seed % 7) - 3);
  return { label: conditions[index]!, temp };
}

export function sanitizeChat(input: string) {
  const blocked = /(https?:\/\/\S+|www\.\S+|\b\S+@\S+\.\S+\b|(?:\+?\d[\s().-]*){8,}|\b(?:fuck|shit|bitch|сука|бля|хуй|пизд)\w*\b)/giu;
  return input.replace(/[<>\u0000-\u001f]/g, "").replace(blocked, "•••").replace(/\s+/g, " ").trim().slice(0, 180);
}
